import { FleetChangeInputRemove, FleetChangeInputUpdate } from "@shared/platform-api/generated"
import { ApiError, CenteredLoader } from "@shared/primitive-components"
import { useEffect } from "react"
import { FleetEntry, useChangeFleetModern } from "../../../api/fleet"
import { Policy } from "../../../api/policy"
import { useAccess, usePolicyAndFleet } from "../../../contexts"

type Props = {
  entries: ReadonlyArray<FleetEntry>
  editIdSet: Set<string>
  addedInError: boolean
  onUpdateComplete: () => void
}

export const MakeUpdateToFleet = ({ entries, editIdSet, addedInError, onUpdateComplete }: Props) => {
  const { policy } = usePolicyAndFleet()

  return (
    <MakeUpdateToFleetModern
      policy={policy}
      entries={entries}
      editIdSet={editIdSet}
      addedInError={addedInError}
      onUpdateComplete={onUpdateComplete}
    />
  )
}

const makeRemove = (entries: ReadonlyArray<FleetEntry>): Array<FleetChangeInputRemove> =>
  entries.map(({ id }) => ({
    kind: `remove`,
    id: id!!,
  }))

const makeUpdate = (entries: ReadonlyArray<FleetEntry>): Array<FleetChangeInputUpdate> =>
  entries.map(({ id, onCoverDate, offCoverDate }) => ({
    kind: `update`,
    onCoverDate,
    offCoverDate,
    id: id!!,
  }))

const makeChanges = (isRemove: boolean, entries: Array<FleetEntry>) =>
  isRemove ? makeRemove(entries) : makeUpdate(entries)

type MakeUpdateToFleetModernProps = Props & { policy: Policy }

const MakeUpdateToFleetModern = ({
  policy,
  entries,
  editIdSet,
  addedInError,
  onUpdateComplete,
}: MakeUpdateToFleetModernProps) => {
  // @Note - IMO this is a bad way of getting the checked entries.
  // We should just be passing whats checked in?
  const filtered = entries.filter((entry) => entry.id && !!editIdSet.has(entry.id))

  const changes = makeChanges(addedInError, filtered)

  const hasAdminAccess = useAccess().hasAdminAccess()
  const [res] = useChangeFleetModern({
    requestedAccessLevel: hasAdminAccess ? `elevated` : `standard`,
    policyId: policy.id,
    changes,
  })

  useEffect(() => {
    if (res.result?.tag !== `ok`) return
    onUpdateComplete()
  }, [res.result?.tag, onUpdateComplete])

  if (res.result?.tag === `err`) {
    // eslint-disable-next-line no-console
    console.log(res.result.error)

    if (res.result.error.type === `INVALID`) {
      switch (res.result.error.payload.type) {
        case `NO_MATCHES`:
          return <ApiError error="VEHICLE_NOT_MATCHED_TO_BAND" />
        case `INVALID_CHANGE`:
          return <ApiError error="INVALID_CHANGE" />
        default:
          return <ApiError />
      }
    }

    return <ApiError />
  }

  if (res.loading || !res.result) return <CenteredLoader />

  return null
}
